import { useT } from "../../../shared/i18n/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Globe, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTRPC } from "../../../shared/api/trpc";
import { Button } from "@/components/ui/button";

// Editor-only controls for a view's read-only public link — the viewShares
// twin of the form view's PublicLinkControls.
export function ViewShareLinkControls({ viewId }: { viewId: string }) {
  const t = useT();
  const trpcClient = useTRPC();
  const queryClient = useQueryClient();
  const linkQuery = useQuery(trpcClient.viewShares.link.queryOptions({ boardViewId: viewId }, { retry: false }));
  const enableMutation = useMutation(trpcClient.viewShares.enableLink.mutationOptions());
  const disableMutation = useMutation(trpcClient.viewShares.disableLink.mutationOptions());
  const [isCopied, setIsCopied] = useState(false);

  // Viewers cannot manage the capability link — the query rejects for them
  // and the whole block stays hidden.
  if (linkQuery.isError || linkQuery.isPending) return null;

  const token = linkQuery.data.token;
  const url = token ? `${window.location.origin}/view/${token}` : null;
  const busy = enableMutation.isPending || disableMutation.isPending;

  async function toggle(enabled: boolean) {
    if (enabled) await enableMutation.mutateAsync({ boardViewId: viewId });
    else await disableMutation.mutateAsync({ boardViewId: viewId });
    await queryClient.invalidateQueries({ queryKey: trpcClient.viewShares.link.queryKey({ boardViewId: viewId }) });
  }

  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border/70 bg-card px-3 py-2.5">
      <Globe className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium">{t("shareLink.publicReadOnly")}</p>
        <p className="truncate text-[11px] text-muted-foreground">
          {url ?? t("shareLink.anyoneCanView")}
        </p>
      </div>
      {url ? (
        <Button
          className="size-6 shrink-0"
          onClick={async () => {
            await navigator.clipboard.writeText(url);
            setIsCopied(true);
            window.setTimeout(() => setIsCopied(false), 1500);
          }}
          size="icon-xs"
          title={t("shareLink.copy")}
          variant="ghost"
        >
          {isCopied ? <Check /> : <Copy />}
        </Button>
      ) : null}
      <Button className="h-6 shrink-0 px-2 text-xs" disabled={busy} onClick={() => void toggle(token === null)} size="sm" variant={token ? "outline" : "default"}>
        {busy ? <Loader2 className="animate-spin" data-icon="inline-start" /> : null}
        {token ? t("shareLink.disable") : t("shareLink.enable")}
      </Button>
    </div>
  );
}
